/**
 * Shared pieces of the connection form's controls (Option C, C2): the native `<select>` and `<textarea>` styled like the
 * app's `Input` (36 px, `text-sm`; native selects stay native — 22 e2e files call `selectOption` on them, rule 3b.3), the
 * checkbox style, and the inline field error. The form's fields are TanStack `form.Field`s; nothing here holds state.
 */
import { inputVariants } from '@/components/ui/input';
import { cn } from '@/lib/utils';

/** The TanStack form API, as the connection components use it (Field, Subscribe, get / setFieldValue, state). */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type ConnectionFormApi = any;

export const SELECT_CLASS = cn(inputVariants(), 'pr-2');
export const TEXTAREA_CLASS = 'block w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring';
export const CHECKBOX_CLASS = 'h-4 w-4 shrink-0 rounded border-input bg-background text-primary focus:ring-primary disabled:cursor-not-allowed disabled:opacity-50';
/** A checkbox and its label on one line, in the control column. */
export const CHECK_ROW_CLASS = 'flex min-h-9 flex-wrap items-center gap-x-2 gap-y-1 text-sm';
export const INVALID_CLASS = 'border-destructive focus-visible:ring-destructive';

/** The first error message on a TanStack field's meta (a string or a zod issue), if any. */
export function firstErrorMessage(meta: { errors?: unknown[] } | undefined): string | undefined {
  const first = meta?.errors?.[0];
  if (typeof first === 'string') return first;
  if (first && typeof first === 'object' && 'message' in first) {
    const message = (first as { message?: unknown }).message;
    if (typeof message === 'string') return message;
  }
  return undefined;
}

export function FieldError({ meta, testId }: { meta: { errors?: unknown[] } | undefined; testId?: string }) {
  const message = firstErrorMessage(meta);
  return message ? <p className="mt-1 text-sm text-destructive" data-testid={testId}>{message}</p> : null;
}
